import { useMemo } from 'react';

import useViewport from 'Hooks/useViewport';

/**
 * Custom hook to calculate the width of each slide in a carousel based on the viewport and settings.
 * @param {Array} allSlides - Array of all slides in the carousel
 * @param {boolean} fullWidth - Whether the carousel should take up the full width
 * @param {number} sliderGap - Gap between slides
 * @param {number} sliderWidth - Total width of the slider
 * @param {number} slidesToShow - Number of slides to show at once
 * @param {number} slideVisiblePercent - Percentage of width for slides on mobile devices
 * @returns {number} Calculated width of slide
 */

export default function useCalculateSlideWidth(
  allSlides,
  fullWidth,
  sliderGap,
  sliderWidth,
  slidesToShow,
  slideVisiblePercent,
) {
  const { isXs, isAboveXs, isAboveMd, width: windowWidth } = useViewport();

  return useMemo(() => {
    const gaps = sliderGap * (slidesToShow - 1);

    // On xs mobile slides default to 85% width. This is so that the next slide is partially visible
    // This uses the windowWidth because the xs breakpoint is fluid
    let width = windowWidth * (slideVisiblePercent || 0.85);

    // For custom slider widths on xs, we need to adjust the slide width accordingly
    if (isXs && sliderWidth < 375) {
      width = sliderWidth * (slideVisiblePercent || 0.85);
    }

    // If slidesToShow is 1, the slide will take up 90% of the sliderWidth
    if (slidesToShow === 1 && isAboveXs) {
      width = sliderWidth * (slideVisiblePercent || 0.9);
    }

    // If slidesToShow is 1 on desktop and fullWidth is true, the slide will take up 100% of the sliderWidth
    if (slidesToShow === 1 && isAboveMd && fullWidth) {
      width = sliderWidth;
    }

    // If slidesToShow is more than 1 on mobile, slides share 90% of the available space so that the next slide is partially visible
    if (slidesToShow > 1 && !isAboveMd) {
      width = ((sliderWidth - gaps) * 0.9) / slidesToShow;
    }

    // If slidesToShow is more than 1 on desktop, share the sliderWidth minus gaps with slidesToShow
    if (slidesToShow > 1 && isAboveMd) {
      width = (sliderWidth - gaps) / slidesToShow;
    }


    // FALLBACK
    // If there is only 1 slide it needs to be 100% width
    // On xs, we want to account for the gap at the edge of the screen
    // On xs with a custom sliderWidth, we want to use the sliderWidth instead of windowWidth
    if (allSlides.length === 1 && slidesToShow === 1 && isXs) width = (windowWidth - 32);
    if (allSlides.length === 1 && slidesToShow === 1 && !isXs) width = sliderWidth;
    if (allSlides.length === 1 && slidesToShow === 1 && isXs && sliderWidth < 375) width = sliderWidth; // eslint-disable-line

    return width;
  }, [
    slidesToShow,
    sliderGap,
    sliderWidth,
    fullWidth,
    allSlides,
    isAboveMd,
    windowWidth,
    isXs,
    isAboveXs,
    slideVisiblePercent,
  ]);
}
